// Product-specific content for each product type
// This file uses translation keys that are resolved at runtime

import iconCollagen from '@/assets/icon-collagen.png';
import iconCeramide from '@/assets/icon-ceramide.png';
import iconVitaminE from '@/assets/icon-vitamin-e.png';
import iconPdrnSalmon from '@/assets/icon-pdrn-salmon.png';
import iconHyaluronicAcid from '@/assets/icon-hyaluronic-acid.png';
import iconNiacinamide from '@/assets/icon-niacinamide.png';
import iconPdrnBundle from '@/assets/icon-pdrn-bundle.png';
import iconTripleHydration from '@/assets/icon-triple-hydration.png';
import iconAntiAgingComplex from '@/assets/icon-anti-aging-complex.png';
import iconPdrnSerum from '@/assets/icon-pdrn-serum.png';
import iconPeptide from '@/assets/icon-peptide.png';
import iconAdenosine from '@/assets/icon-adenosine.png';
import iconGlassSkin from '@/assets/icon-glass-skin.png';
import iconVitaminB12 from '@/assets/icon-vitamin-b12.png';
import iconVitaminC from '@/assets/icon-vitamin-c.png';
import iconAvocado from '@/assets/icon-avocado.png';
import iconChamomile from '@/assets/icon-chamomile.png';

// Customer review images (UGC photos shared by customers)
import reviewSetShelf from '@/assets/reviews/review-set-shelf.png';
import reviewSetBathroom from '@/assets/reviews/review-set-bathroom.png';
import reviewCapsuleHand from '@/assets/reviews/review-capsule-hand.jpeg';
import reviewSetSink from '@/assets/reviews/review-set-sink.jpeg';
import reviewSetMarble from '@/assets/reviews/review-set-marble.jpeg';

// Mask review images (UGC photos shared by customers)
import reviewMaskCouch from '@/assets/reviews/review-mask-couch-selfie.jpeg';
import reviewMaskGlowBed from '@/assets/reviews/review-mask-glow-bed.png';
import reviewMaskPackageBack from '@/assets/reviews/review-mask-package-back.jpg';
import reviewMaskHandHeld from '@/assets/reviews/review-mask-hand-held.jpg';
import reviewMaskCarSelfie from '@/assets/reviews/review-mask-car-selfie.png';
import reviewMaskPacketGlow from '@/assets/reviews/review-mask-packet-glow.jpeg';
import reviewMaskCoupleMirror from '@/assets/reviews/review-mask-couple-mirror.png';

// Cream review images (UGC photos shared by customers)
import reviewCreamHand from '@/assets/reviews/review-cream-hand.jpeg';
import reviewCreamDip from '@/assets/reviews/review-cream-dip.png';
import reviewCreamTexture from '@/assets/reviews/review-cream-texture.jpeg';
import reviewCreamVanity from '@/assets/reviews/review-cream-vanity.png';
import reviewCreamSunlight from '@/assets/reviews/review-cream-sunlight.png';
import reviewCreamApplied from '@/assets/reviews/review-cream-applied.jpeg';

interface HowToUseStep {
  step: number;
  titleKey: string;
  descriptionKey: string;
}

interface Ingredient {
  nameKey: string;
  descriptionKey: string;
  icon: 'sparkles' | 'droplets' | 'zap';
  iconColor: string;
  image?: string;
}

interface TimelineItem {
  timeKey: string;
  descriptionKey: string;
}

interface Review {
  nameKey: string;
  rating: number;
  textKey: string;
  verified: boolean;
  image?: string;
  reviewImage?: string;
}

interface MediaItem {
  type: 'image' | 'gif' | 'video';
  placeholder: boolean;
  captionKey: string;
}

interface FAQItem {
  questionKey: string;
  answerKey: string;
}

interface ProductSpec {
  volumeKey: string;
  skinTypeKey: string;
  madeInKey: string;
  shelfLifeKey: string;
}

interface HighlightItem {
  textKey: string;
}

interface ScienceInsideCard {
  icon: string;
  titleKey: string;
  subtitleKey: string;
  points: string[];
}

interface ScienceInsideData {
  titleKey: string;
  subtitleKey: string;
  taglineKey: string;
  cards: ScienceInsideCard[];
}

interface ProductContent {
  howToUse: HowToUseStep[];
  ingredients: Ingredient[];
  resultsTimeline: TimelineItem[];
  reviews: Review[];
  mediaGallery: MediaItem[];
  faq: FAQItem[];
  specs: ProductSpec;
  highlights: HighlightItem[];
  scienceInside?: ScienceInsideData;
}

// Sheet Masks content
const sheetMasksContent: ProductContent = {
  specs: {
    volumeKey: 'productContent.specs.masks.volume',
    skinTypeKey: 'productContent.specs.allSkinTypes',
    madeInKey: 'productContent.specs.madeInKorea',
    shelfLifeKey: 'productContent.specs.masks.shelfLife',
  },
  highlights: [
    { textKey: 'productContent.highlights.masks.1' },
    { textKey: 'productContent.highlights.masks.2' },
    { textKey: 'productContent.highlights.masks.3' },
    { textKey: 'productContent.highlights.masks.4' },
    { textKey: 'productContent.highlights.masks.5' },
  ],
  howToUse: [
    { step: 1, titleKey: "productContent.masks.step1.title", descriptionKey: "productContent.masks.step1.desc" },
    { step: 2, titleKey: "productContent.masks.step2.title", descriptionKey: "productContent.masks.step2.desc" },
    { step: 3, titleKey: "productContent.masks.step3.title", descriptionKey: "productContent.masks.step3.desc" },
    { step: 4, titleKey: "productContent.masks.step4.title", descriptionKey: "productContent.masks.step4.desc" },
  ],
  ingredients: [
    { nameKey: "productContent.ingredient.pdrn.name", descriptionKey: "productContent.ingredient.pdrn.desc", icon: 'sparkles', iconColor: 'pink', image: iconPdrnSalmon },
    { nameKey: "productContent.ingredient.vitaminB12.name", descriptionKey: "productContent.ingredient.vitaminB12.desc", icon: 'sparkles', iconColor: 'pink', image: iconVitaminB12 },
    { nameKey: "productContent.ingredient.niacinamide.name", descriptionKey: "productContent.ingredient.niacinamide.desc", icon: 'zap', iconColor: 'amber', image: iconNiacinamide },
    { nameKey: "productContent.ingredient.sodiumHyaluronate.name", descriptionKey: "productContent.ingredient.sodiumHyaluronate.desc", icon: 'droplets', iconColor: 'blue', image: iconHyaluronicAcid },
    { nameKey: "productContent.ingredient.vitaminC.name", descriptionKey: "productContent.ingredient.vitaminC.desc", icon: 'zap', iconColor: 'amber', image: iconVitaminC },
  ],
  resultsTimeline: [
    { timeKey: "productContent.timeline.immediately", descriptionKey: "productContent.masks.result1" },
    { timeKey: "productContent.timeline.24hours", descriptionKey: "productContent.masks.result2" },
    { timeKey: "productContent.timeline.weekly", descriptionKey: "productContent.masks.result3" },
    { timeKey: "productContent.timeline.4weeks", descriptionKey: "productContent.masks.result4" },
  ],
  mediaGallery: [
    { type: 'image', placeholder: true, captionKey: 'productContent.masks.media1' },
    { type: 'gif', placeholder: true, captionKey: 'productContent.masks.media2' },
    { type: 'video', placeholder: true, captionKey: 'productContent.masks.media3' },
  ],
  reviews: [
    { nameKey: "productContent.masks.reviewer1.name", rating: 5, textKey: "productContent.masks.review1", verified: true, image: "https://randomuser.me/api/portraits/women/12.jpg", reviewImage: reviewMaskCouch },
    { nameKey: "productContent.masks.reviewer2.name", rating: 5, textKey: "productContent.masks.review2", verified: true, image: "https://randomuser.me/api/portraits/women/33.jpg", reviewImage: reviewMaskGlowBed },
    { nameKey: "productContent.masks.reviewer3.name", rating: 5, textKey: "productContent.masks.review3", verified: true, image: "https://randomuser.me/api/portraits/women/44.jpg", reviewImage: reviewMaskPackageBack },
    { nameKey: "productContent.masks.reviewer4.name", rating: 4, textKey: "productContent.masks.review4", verified: true, image: "https://randomuser.me/api/portraits/women/82.jpg" },
    { nameKey: "productContent.masks.reviewer5.name", rating: 5, textKey: "productContent.masks.review5", verified: true, image: "https://randomuser.me/api/portraits/women/67.jpg", reviewImage: reviewMaskHandHeld },
    { nameKey: "productContent.masks.reviewer6.name", rating: 5, textKey: "productContent.masks.review6", verified: true, image: "https://randomuser.me/api/portraits/women/71.jpg", reviewImage: reviewMaskCarSelfie },
    { nameKey: "productContent.masks.reviewer7.name", rating: 4, textKey: "productContent.masks.review7", verified: true, image: "https://randomuser.me/api/portraits/women/55.jpg", reviewImage: reviewMaskPacketGlow },
    { nameKey: "productContent.masks.reviewer8.name", rating: 5, textKey: "productContent.masks.review8", verified: true, image: "https://randomuser.me/api/portraits/women/90.jpg", reviewImage: reviewMaskCoupleMirror },
  ],
  faq: [
    { questionKey: "productContent.masks.faq1.q", answerKey: "productContent.masks.faq1.a" },
    { questionKey: "productContent.masks.faq2.q", answerKey: "productContent.masks.faq2.a" },
    { questionKey: "productContent.masks.faq3.q", answerKey: "productContent.masks.faq3.a" },
    { questionKey: "productContent.masks.faq4.q", answerKey: "productContent.masks.faq4.a" },
    { questionKey: "productContent.masks.faq5.q", answerKey: "productContent.masks.faq5.a" },
  ],
  scienceInside: {
    titleKey: 'scienceInside.title',
    subtitleKey: 'scienceInside.masks.subtitle',
    taglineKey: 'scienceInside.masks.tagline',
    cards: [
      {
        icon: iconVitaminB12,
        titleKey: 'scienceInside.masks.card1.title',
        subtitleKey: 'scienceInside.masks.card1.subtitle',
        points: [
          'scienceInside.masks.card1.point1',
          'scienceInside.masks.card1.point2',
          'scienceInside.masks.card1.point3',
          'scienceInside.masks.card1.point4',
        ],
      },
      {
        icon: iconNiacinamide,
        titleKey: 'scienceInside.masks.card2.title',
        subtitleKey: 'scienceInside.masks.card2.subtitle',
        points: [
          'scienceInside.masks.card2.point1',
          'scienceInside.masks.card2.point2',
          'scienceInside.masks.card2.point3',
          'scienceInside.masks.card2.point4',
        ],
      },
      {
        icon: iconGlassSkin,
        titleKey: 'scienceInside.masks.card3.title',
        subtitleKey: 'scienceInside.masks.card3.subtitle',
        points: [
          'scienceInside.masks.card3.point1',
          'scienceInside.masks.card3.point2',
          'scienceInside.masks.card3.point3',
          'scienceInside.masks.card3.point4',
        ],
      },
    ],
  },
};

// Serum content
const serumContent: ProductContent = {
  specs: {
    volumeKey: 'productContent.specs.serum.volume',
    skinTypeKey: 'productContent.specs.allSkinTypes',
    madeInKey: 'productContent.specs.madeInKorea',
    shelfLifeKey: 'productContent.specs.serum.shelfLife',
  },
  highlights: [
    { textKey: 'productContent.highlights.serum.1' },
    { textKey: 'productContent.highlights.serum.2' },
    { textKey: 'productContent.highlights.serum.3' },
    { textKey: 'productContent.highlights.serum.4' },
    { textKey: 'productContent.highlights.serum.5' },
  ],
  howToUse: [
    { step: 1, titleKey: "productContent.serum.step1.title", descriptionKey: "productContent.serum.step1.desc" },
    { step: 2, titleKey: "productContent.serum.step2.title", descriptionKey: "productContent.serum.step2.desc" },
    { step: 3, titleKey: "productContent.serum.step3.title", descriptionKey: "productContent.serum.step3.desc" },
    { step: 4, titleKey: "productContent.serum.step4.title", descriptionKey: "productContent.serum.step4.desc" },
  ],
  ingredients: [
    { nameKey: "productContent.ingredient.pdrn.name", descriptionKey: "productContent.ingredient.pdrnSerum.desc", icon: 'sparkles', iconColor: 'pink', image: iconPdrnSerum },
    { nameKey: "productContent.ingredient.peptide.name", descriptionKey: "productContent.ingredient.peptide.desc", icon: 'zap', iconColor: 'amber', image: iconPeptide },
    { nameKey: "productContent.ingredient.adenosine.name", descriptionKey: "productContent.ingredient.adenosine.desc", icon: 'droplets', iconColor: 'blue', image: iconAdenosine },
  ],
  resultsTimeline: [
    { timeKey: "productContent.timeline.day1", descriptionKey: "productContent.serum.result1" },
    { timeKey: "productContent.timeline.week2", descriptionKey: "productContent.serum.result2" },
    { timeKey: "productContent.timeline.week4", descriptionKey: "productContent.serum.result3" },
    { timeKey: "productContent.timeline.week8", descriptionKey: "productContent.serum.result4" },
  ],
  mediaGallery: [
    { type: 'image', placeholder: true, captionKey: 'productContent.serum.media1' },
    { type: 'gif', placeholder: true, captionKey: 'productContent.serum.media2' },
    { type: 'video', placeholder: true, captionKey: 'productContent.serum.media3' },
  ],
  reviews: [
    { nameKey: "productContent.serum.reviewer1.name", rating: 5, textKey: "productContent.serum.review1", verified: true, image: "https://randomuser.me/api/portraits/women/15.jpg" },
    { nameKey: "productContent.serum.reviewer2.name", rating: 5, textKey: "productContent.serum.review2", verified: true, image: "https://randomuser.me/api/portraits/women/28.jpg" },
    { nameKey: "productContent.serum.reviewer3.name", rating: 5, textKey: "productContent.serum.review3", verified: true, image: "https://randomuser.me/api/portraits/women/36.jpg" },
    { nameKey: "productContent.serum.reviewer4.name", rating: 4, textKey: "productContent.serum.review4", verified: true, image: "https://randomuser.me/api/portraits/women/48.jpg" },
    { nameKey: "productContent.serum.reviewer5.name", rating: 5, textKey: "productContent.serum.review5", verified: true, image: "https://randomuser.me/api/portraits/women/57.jpg" },
    { nameKey: "productContent.serum.reviewer6.name", rating: 5, textKey: "productContent.serum.review6", verified: true, image: "https://randomuser.me/api/portraits/women/63.jpg" },
    { nameKey: "productContent.serum.reviewer7.name", rating: 4, textKey: "productContent.serum.review7", verified: true, image: "https://randomuser.me/api/portraits/women/74.jpg" },
    { nameKey: "productContent.serum.reviewer8.name", rating: 5, textKey: "productContent.serum.review8", verified: true, image: "https://randomuser.me/api/portraits/women/85.jpg" },
  ],
  faq: [
    { questionKey: "productContent.serum.faq1.q", answerKey: "productContent.serum.faq1.a" },
    { questionKey: "productContent.serum.faq2.q", answerKey: "productContent.serum.faq2.a" },
    { questionKey: "productContent.serum.faq3.q", answerKey: "productContent.serum.faq3.a" },
    { questionKey: "productContent.serum.faq4.q", answerKey: "productContent.serum.faq4.a" },
    { questionKey: "productContent.serum.faq5.q", answerKey: "productContent.serum.faq5.a" },
    { questionKey: "productContent.serum.faq6.q", answerKey: "productContent.serum.faq6.a" },
  ],
  scienceInside: {
    titleKey: 'scienceInside.title',
    subtitleKey: 'scienceInside.subtitle',
    taglineKey: 'scienceInside.tagline',
    cards: [
      {
        icon: iconPeptide,
        titleKey: 'scienceInside.peptides.title',
        subtitleKey: 'scienceInside.peptides.subtitle',
        points: [
          'scienceInside.peptides.point1',
          'scienceInside.peptides.point2',
          'scienceInside.peptides.point3',
          'scienceInside.peptides.point4',
        ],
      },
      {
        icon: iconPdrnSalmon,
        titleKey: 'scienceInside.pdrn.title',
        subtitleKey: 'scienceInside.pdrn.subtitle',
        points: [
          'scienceInside.pdrn.point1',
          'scienceInside.pdrn.point2',
          'scienceInside.pdrn.point3',
          'scienceInside.pdrn.point4',
        ],
      },
      {
        icon: iconGlassSkin,
        titleKey: 'scienceInside.combo.title',
        subtitleKey: 'scienceInside.combo.subtitle',
        points: [
          'scienceInside.combo.point1',
          'scienceInside.combo.point2',
          'scienceInside.combo.point3',
          'scienceInside.combo.point4',
        ],
      },
    ],
  },
};

// Cream content
const creamContent: ProductContent = {
  specs: {
    volumeKey: 'productContent.specs.cream.volume',
    skinTypeKey: 'productContent.specs.allSkinTypes',
    madeInKey: 'productContent.specs.madeInKorea',
    shelfLifeKey: 'productContent.specs.cream.shelfLife',
  },
  highlights: [
    { textKey: 'productContent.highlights.cream.1' },
    { textKey: 'productContent.highlights.cream.2' },
    { textKey: 'productContent.highlights.cream.3' },
    { textKey: 'productContent.highlights.cream.4' },
    { textKey: 'productContent.highlights.cream.5' },
  ],
  howToUse: [
    { step: 1, titleKey: "productContent.cream.step1.title", descriptionKey: "productContent.cream.step1.desc" },
    { step: 2, titleKey: "productContent.cream.step2.title", descriptionKey: "productContent.cream.step2.desc" },
    { step: 3, titleKey: "productContent.cream.step3.title", descriptionKey: "productContent.cream.step3.desc" },
    { step: 4, titleKey: "productContent.cream.step4.title", descriptionKey: "productContent.cream.step4.desc" },
  ],
  ingredients: [
    { nameKey: "productContent.ingredient.pdrn.name", descriptionKey: "productContent.ingredient.pdrnCream.desc", icon: 'sparkles', iconColor: 'pink', image: iconPdrnSalmon },
    { nameKey: "productContent.ingredient.niacinamide.name", descriptionKey: "productContent.ingredient.niacinamideCream.desc", icon: 'zap', iconColor: 'amber', image: iconNiacinamide },
    { nameKey: "productContent.ingredient.hydrolyzedCollagen.name", descriptionKey: "productContent.ingredient.hydrolyzedCollagen.desc", icon: 'sparkles', iconColor: 'pink', image: iconCollagen },
    { nameKey: "productContent.ingredient.avocado.name", descriptionKey: "productContent.ingredient.avocado.desc", icon: 'droplets', iconColor: 'blue', image: iconAvocado },
    { nameKey: "productContent.ingredient.chamomile.name", descriptionKey: "productContent.ingredient.chamomile.desc", icon: 'droplets', iconColor: 'emerald', image: iconChamomile },
  ],
  resultsTimeline: [
    { timeKey: "productContent.timeline.day1", descriptionKey: "productContent.cream.result1" },
    { timeKey: "productContent.timeline.week2", descriptionKey: "productContent.cream.result2" },
    { timeKey: "productContent.timeline.week4", descriptionKey: "productContent.cream.result3" },
    { timeKey: "productContent.timeline.week8", descriptionKey: "productContent.cream.result4" },
  ],
  mediaGallery: [
    { type: 'image', placeholder: true, captionKey: 'productContent.cream.media1' },
    { type: 'gif', placeholder: true, captionKey: 'productContent.cream.media2' },
    { type: 'video', placeholder: true, captionKey: 'productContent.cream.media3' },
  ],
  reviews: [
    { nameKey: "productContent.cream.reviewer1.name", rating: 5, textKey: "productContent.cream.review1", verified: true, image: "https://randomuser.me/api/portraits/women/19.jpg", reviewImage: reviewCreamHand },
    { nameKey: "productContent.cream.reviewer2.name", rating: 5, textKey: "productContent.cream.review2", verified: true, image: "https://randomuser.me/api/portraits/women/25.jpg", reviewImage: reviewCreamDip },
    { nameKey: "productContent.cream.reviewer3.name", rating: 5, textKey: "productContent.cream.review3", verified: true, image: "https://randomuser.me/api/portraits/women/39.jpg", reviewImage: reviewCreamTexture },
    { nameKey: "productContent.cream.reviewer4.name", rating: 4, textKey: "productContent.cream.review4", verified: true, image: "https://randomuser.me/api/portraits/women/52.jpg" },
    { nameKey: "productContent.cream.reviewer5.name", rating: 5, textKey: "productContent.cream.review5", verified: true, image: "https://randomuser.me/api/portraits/women/61.jpg", reviewImage: reviewCreamVanity },
    { nameKey: "productContent.cream.reviewer6.name", rating: 5, textKey: "productContent.cream.review6", verified: true, image: "https://randomuser.me/api/portraits/women/76.jpg", reviewImage: reviewCreamSunlight },
    { nameKey: "productContent.cream.reviewer7.name", rating: 4, textKey: "productContent.cream.review7", verified: true, image: "https://randomuser.me/api/portraits/women/88.jpg" },
    { nameKey: "productContent.cream.reviewer8.name", rating: 5, textKey: "productContent.cream.review8", verified: true, image: "https://randomuser.me/api/portraits/women/93.jpg", reviewImage: reviewCreamApplied },
  ],
  faq: [
    { questionKey: "productContent.cream.faq1.q", answerKey: "productContent.cream.faq1.a" },
    { questionKey: "productContent.cream.faq2.q", answerKey: "productContent.cream.faq2.a" },
    { questionKey: "productContent.cream.faq3.q", answerKey: "productContent.cream.faq3.a" },
    { questionKey: "productContent.cream.faq4.q", answerKey: "productContent.cream.faq4.a" },
    { questionKey: "productContent.cream.faq5.q", answerKey: "productContent.cream.faq5.a" },
  ],
  scienceInside: {
    titleKey: 'scienceInside.title',
    subtitleKey: 'scienceInside.cream.subtitle',
    taglineKey: 'scienceInside.cream.tagline',
    cards: [
      {
        icon: iconCollagen,
        titleKey: 'scienceInside.cream.card1.title',
        subtitleKey: 'scienceInside.cream.card1.subtitle',
        points: [
          'scienceInside.cream.card1.point1',
          'scienceInside.cream.card1.point2',
          'scienceInside.cream.card1.point3',
          'scienceInside.cream.card1.point4',
        ],
      },
      {
        icon: iconCeramide,
        titleKey: 'scienceInside.cream.card2.title',
        subtitleKey: 'scienceInside.cream.card2.subtitle',
        points: [
          'scienceInside.cream.card2.point1',
          'scienceInside.cream.card2.point2',
          'scienceInside.cream.card2.point3',
          'scienceInside.cream.card2.point4',
        ],
      },
      {
        icon: iconAntiAgingComplex,
        titleKey: 'scienceInside.cream.card3.title',
        subtitleKey: 'scienceInside.cream.card3.subtitle',
        points: [
          'scienceInside.cream.card3.point1',
          'scienceInside.cream.card3.point2',
          'scienceInside.cream.card3.point3',
          'scienceInside.cream.card3.point4',
        ],
      },
    ],
  },
};

// Bundle content
const bundleContent: ProductContent = {
  specs: {
    volumeKey: 'productContent.specs.bundle.volume',
    skinTypeKey: 'productContent.specs.allSkinTypes',
    madeInKey: 'productContent.specs.madeInKorea',
    shelfLifeKey: 'productContent.specs.bundle.shelfLife',
  },
  highlights: [
    { textKey: 'productContent.highlights.bundle.1' },
    { textKey: 'productContent.highlights.bundle.2' },
    { textKey: 'productContent.highlights.bundle.3' },
    { textKey: 'productContent.highlights.bundle.4' },
    { textKey: 'productContent.highlights.bundle.5' },
  ],
  howToUse: [
    { step: 1, titleKey: "productContent.bundle.step1.title", descriptionKey: "productContent.bundle.step1.desc" },
    { step: 2, titleKey: "productContent.bundle.step2.title", descriptionKey: "productContent.bundle.step2.desc" },
    { step: 3, titleKey: "productContent.bundle.step3.title", descriptionKey: "productContent.bundle.step3.desc" },
    { step: 4, titleKey: "productContent.bundle.step4.title", descriptionKey: "productContent.bundle.step4.desc" },
  ],
  ingredients: [
    { nameKey: "productContent.ingredient.pdrn.name", descriptionKey: "productContent.ingredient.pdrnBundle.desc", icon: 'sparkles', iconColor: 'pink', image: iconPdrnBundle },
    { nameKey: "productContent.ingredient.vitaminB12.name", descriptionKey: "productContent.ingredient.vitaminB12Bundle.desc", icon: 'sparkles', iconColor: 'pink', image: iconVitaminB12 },
    { nameKey: "productContent.ingredient.niacinamide.name", descriptionKey: "productContent.ingredient.niacinamideBundle.desc", icon: 'zap', iconColor: 'amber', image: iconNiacinamide },
    { nameKey: "productContent.ingredient.peptide.name", descriptionKey: "productContent.ingredient.peptideBundle.desc", icon: 'zap', iconColor: 'amber', image: iconPeptide },
    { nameKey: "productContent.ingredient.hydrolyzedCollagen.name", descriptionKey: "productContent.ingredient.hydrolyzedCollagenBundle.desc", icon: 'sparkles', iconColor: 'pink', image: iconCollagen },
  ],
  resultsTimeline: [
    { timeKey: "productContent.timeline.week1", descriptionKey: "productContent.bundle.result1" },
    { timeKey: "productContent.timeline.week4", descriptionKey: "productContent.bundle.result2" },
    { timeKey: "productContent.timeline.week8", descriptionKey: "productContent.bundle.result3" },
    { timeKey: "productContent.timeline.week12", descriptionKey: "productContent.bundle.result4" },
  ],
  mediaGallery: [
    { type: 'image', placeholder: true, captionKey: 'productContent.bundle.media1' },
    { type: 'gif', placeholder: true, captionKey: 'productContent.bundle.media2' },
    { type: 'video', placeholder: true, captionKey: 'productContent.bundle.media3' },
  ],
  reviews: [
    { nameKey: "productContent.bundle.reviewer1.name", rating: 5, textKey: "productContent.bundle.review1", verified: true, image: "https://randomuser.me/api/portraits/women/22.jpg", reviewImage: reviewSetShelf },
    { nameKey: "productContent.bundle.reviewer2.name", rating: 5, textKey: "productContent.bundle.review2", verified: true, image: "https://randomuser.me/api/portraits/women/31.jpg" },
    { nameKey: "productContent.bundle.reviewer3.name", rating: 5, textKey: "productContent.bundle.review3", verified: true, image: "https://randomuser.me/api/portraits/women/42.jpg", reviewImage: reviewSetBathroom },
    { nameKey: "productContent.bundle.reviewer4.name", rating: 4, textKey: "productContent.bundle.review4", verified: true, image: "https://randomuser.me/api/portraits/women/53.jpg" },
    { nameKey: "productContent.bundle.reviewer5.name", rating: 5, textKey: "productContent.bundle.review5", verified: true, image: "https://randomuser.me/api/portraits/women/65.jpg", reviewImage: reviewCapsuleHand },
    { nameKey: "productContent.bundle.reviewer6.name", rating: 5, textKey: "productContent.bundle.review6", verified: true, image: "https://randomuser.me/api/portraits/women/78.jpg", reviewImage: reviewSetMarble },
    { nameKey: "productContent.bundle.reviewer7.name", rating: 5, textKey: "productContent.bundle.review7", verified: true, image: "https://randomuser.me/api/portraits/women/84.jpg" },
    { nameKey: "productContent.bundle.reviewer8.name", rating: 4, textKey: "productContent.bundle.review8", verified: true, image: "https://randomuser.me/api/portraits/women/95.jpg", reviewImage: reviewSetSink },
  ],
  faq: [
    { questionKey: "productContent.bundle.faq1.q", answerKey: "productContent.bundle.faq1.a" },
    { questionKey: "productContent.bundle.faq2.q", answerKey: "productContent.bundle.faq2.a" },
    { questionKey: "productContent.bundle.faq3.q", answerKey: "productContent.bundle.faq3.a" },
    { questionKey: "productContent.bundle.faq4.q", answerKey: "productContent.bundle.faq4.a" },
    { questionKey: "productContent.bundle.faq5.q", answerKey: "productContent.bundle.faq5.a" },
  ],
  scienceInside: {
    titleKey: 'scienceInside.title',
    subtitleKey: 'scienceInside.bundle.subtitle',
    taglineKey: 'scienceInside.bundle.tagline',
    cards: [
      {
        icon: iconPdrnBundle,
        titleKey: 'scienceInside.bundle.card1.title',
        subtitleKey: 'scienceInside.bundle.card1.subtitle',
        points: [
          'scienceInside.bundle.card1.point1',
          'scienceInside.bundle.card1.point2',
          'scienceInside.bundle.card1.point3',
          'scienceInside.bundle.card1.point4',
        ],
      },
      {
        icon: iconTripleHydration,
        titleKey: 'scienceInside.bundle.card2.title',
        subtitleKey: 'scienceInside.bundle.card2.subtitle',
        points: [
          'scienceInside.bundle.card2.point1',
          'scienceInside.bundle.card2.point2',
          'scienceInside.bundle.card2.point3',
          'scienceInside.bundle.card2.point4',
        ],
      },
      {
        icon: iconAntiAgingComplex,
        titleKey: 'scienceInside.bundle.card3.title',
        subtitleKey: 'scienceInside.bundle.card3.subtitle',
        points: [
          'scienceInside.bundle.card3.point1',
          'scienceInside.bundle.card3.point2',
          'scienceInside.bundle.card3.point3',
          'scienceInside.bundle.card3.point4',
        ],
      },
    ],
  },
};

// Default content for unknown products
const defaultContent: ProductContent = {
  specs: {
    volumeKey: 'productContent.specs.default.volume',
    skinTypeKey: 'productContent.specs.allSkinTypes',
    madeInKey: 'productContent.specs.madeInKorea',
    shelfLifeKey: 'productContent.specs.default.shelfLife',
  },
  highlights: [
    { textKey: 'productContent.highlights.default.1' },
    { textKey: 'productContent.highlights.default.2' },
    { textKey: 'productContent.highlights.default.3' },
    { textKey: 'productContent.highlights.default.4' },
  ],
  howToUse: [
    { step: 1, titleKey: "productContent.default.step1.title", descriptionKey: "productContent.default.step1.desc" },
    { step: 2, titleKey: "productContent.default.step2.title", descriptionKey: "productContent.default.step2.desc" },
    { step: 3, titleKey: "productContent.default.step3.title", descriptionKey: "productContent.default.step3.desc" },
    { step: 4, titleKey: "productContent.default.step4.title", descriptionKey: "productContent.default.step4.desc" },
  ],
  ingredients: [
    { nameKey: "productContent.ingredient.pdrn.name", descriptionKey: "productContent.ingredient.pdrn.desc", icon: 'sparkles', iconColor: 'pink' },
    { nameKey: "productContent.ingredient.hyaluronic.name", descriptionKey: "productContent.ingredient.hyaluronic.desc", icon: 'droplets', iconColor: 'blue' },
    { nameKey: "productContent.ingredient.peptide.name", descriptionKey: "productContent.ingredient.peptide.desc", icon: 'zap', iconColor: 'amber' },
  ],
  resultsTimeline: [
    { timeKey: "productContent.timeline.day1", descriptionKey: "productContent.default.result1" },
    { timeKey: "productContent.timeline.week2", descriptionKey: "productContent.default.result2" },
    { timeKey: "productContent.timeline.week4", descriptionKey: "productContent.default.result3" },
    { timeKey: "productContent.timeline.week8", descriptionKey: "productContent.default.result4" },
  ],
  mediaGallery: [
    { type: 'image', placeholder: true, captionKey: 'productContent.default.media1' },
    { type: 'gif', placeholder: true, captionKey: 'productContent.default.media2' },
    { type: 'video', placeholder: true, captionKey: 'productContent.default.media3' },
  ],
  reviews: [
    { nameKey: "productContent.default.reviewer1.name", rating: 5, textKey: "productContent.default.review1", verified: true, image: "https://randomuser.me/api/portraits/women/10.jpg" },
    { nameKey: "productContent.default.reviewer2.name", rating: 5, textKey: "productContent.default.review2", verified: true, image: "https://randomuser.me/api/portraits/women/20.jpg" },
    { nameKey: "productContent.default.reviewer3.name", rating: 5, textKey: "productContent.default.review3", verified: true, image: "https://randomuser.me/api/portraits/women/30.jpg" },
  ],
  faq: [
    { questionKey: "productContent.default.faq1.q", answerKey: "productContent.default.faq1.a" },
    { questionKey: "productContent.default.faq2.q", answerKey: "productContent.default.faq2.a" },
    { questionKey: "productContent.default.faq3.q", answerKey: "productContent.default.faq3.a" },
  ],
  scienceInside: {
    titleKey: 'scienceInside.title',
    subtitleKey: 'scienceInside.default.subtitle',
    taglineKey: 'scienceInside.default.tagline',
    cards: [
      {
        icon: iconPdrnSalmon,
        titleKey: 'scienceInside.pdrn.title',
        subtitleKey: 'scienceInside.pdrn.subtitle',
        points: [
          'scienceInside.pdrn.point1',
          'scienceInside.pdrn.point2',
          'scienceInside.pdrn.point3',
          'scienceInside.pdrn.point4',
        ],
      },
      {
        icon: iconGlassSkin,
        titleKey: 'scienceInside.combo.title',
        subtitleKey: 'scienceInside.combo.subtitle',
        points: [
          'scienceInside.combo.point1',
          'scienceInside.combo.point2',
          'scienceInside.combo.point3',
          'scienceInside.combo.point4',
        ],
      },
    ],
  },
};

import { getProductType } from './productRegistry';

// Exact handle → content mapping (fastest lookup)
const HANDLE_TO_CONTENT: Record<string, ProductContent> = {
  'pdrn-pink-sheet-masks-10-pack': sheetMasksContent,
  'pink-glow-serum-with-pdrn-peptides': serumContent,
  'smart-capsule-cream-with-collagen': creamContent,
  'complete-pdrn-trio-bundle': bundleContent,
};

// Type → content fallback (for keyword-matched handles)
const TYPE_TO_CONTENT: Record<string, ProductContent> = {
  masks: sheetMasksContent,
  serum: serumContent,
  cream: creamContent,
  bundle: bundleContent,
};

export function getProductContent(handle: string): ProductContent {
  // 1. Exact handle match
  if (HANDLE_TO_CONTENT[handle]) return HANDLE_TO_CONTENT[handle];
  // 2. Type-based match (keyword fallback via registry)
  const type = getProductType(handle);
  if (TYPE_TO_CONTENT[type]) return TYPE_TO_CONTENT[type];
  // 3. Default fallback
  return defaultContent;
}

export type { ProductContent, HowToUseStep, Ingredient, TimelineItem, Review, MediaItem, FAQItem, ProductSpec, HighlightItem, ScienceInsideData, ScienceInsideCard };
